import type { ComponentProps } from "react";
import { t } from "ttag";

import { Input } from "@/shared/ui/form/input";
import { SelectPrimitive } from "./";

interface SelectOption<T> {
  value: T;
  label: string;
  disabled?: boolean;
}

type SelectTrigger = ComponentProps<typeof SelectPrimitive.Trigger>;

type SelectProps<T extends SelectTrigger["value"] | number> = Omit<
  SelectTrigger,
  "onValueChange" | "value" | "onChange"
> & {
  options?: readonly SelectOption<T>[];
  placeholder?: string;
  value?: T;
  onChange: (value: T) => void;
  isLoading?: boolean;
  readOnly?: boolean;
  contentClassName?: string;
};

export const Select = <T extends string | number>({
  options = [],
  placeholder,
  value,
  onChange,
  className,
  contentClassName,
  isLoading,
  readOnly,
  leftIcon,
  id,
  ...props
}: SelectProps<T>) => {
  if (readOnly) {
    const label = options.find((option) => option.value === value)?.label;

    return (
      <Input.Root readOnly value={label} id={id}>
        <Input.Slot>{leftIcon}</Input.Slot>
      </Input.Root>
    );
  }

  const handleChange = (value: T) => {
    if (typeof options?.[0]?.value === "number") {
      return onChange(Number(value) as T);
    }

    onChange(value);
  };

  return (
    <SelectPrimitive.Root
      value={value as never}
      onValueChange={(value) => handleChange(value as T)}
    >
      <SelectPrimitive.Trigger
        isLoading={isLoading}
        className={className}
        leftIcon={leftIcon}
        id={id}
        {...props}
      >
        <SelectPrimitive.Value
          placeholder={
            isLoading
              ? t`Loading options...`
              : (placeholder ?? t`Select an option`)
          }
        />
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Content className={contentClassName}>
        {!options.length && (
          <div className="py-6 text-center" role="presentation">
            {t`No options available`}
          </div>
        )}

        {options.map((option) => (
          <SelectPrimitive.Item
            key={option.value}
            value={option.value as never}
            disabled={option.disabled}
          >
            {option.label}
          </SelectPrimitive.Item>
        ))}
      </SelectPrimitive.Content>
    </SelectPrimitive.Root>
  );
};
